import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import FullScreenModal from 'components/consumer/FullScreenModal';
import useModularPDPCustomerPhotosAndReviews from 'data-hooks/useModularPDPCustomerPhotosAndReviews';
import useProductEFGInternalStatus from 'data-hooks/useProductEFGInternalStatus';
import useAllOptions from 'data-hooks/useAllOptions';
import { getOptionValueByNameAndOptionType, getSKUForMaterialOrOptionId, getProductOptionById } from 'commons/productUtils';
import useProductCustomerPhotosReviews from 'data-hooks/useProductCustomerPhotosReviews';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import PDPCustomerPhotosReviewsModalUI from './PDPCustomerPhotosReviewsModalUI';
import usePdpIsModularBYOProduct from '../../state/context/usePdpIsModularBYOProduct';
import usePDPOptions from '../../state/context/usePDPOptions';
const PDPCustomerPhotosReviewsModal = ({
  productId,
  productName,
  imgIndex,
  reviewIndex,
  toggleModal,
  totalReviews,
  averageRating,
  selectedMaterial
}) => {
  const location = useLocation();
  const {
    loading,
    data
  } = useProductCustomerPhotosReviews(productId);
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const {
    loading: optionsLoading,
    data: {
      options = [],
      stock,
      upholsteryOptionId
    } = {}
  } = usePDPOptions();
  const {
    loading: EFGLoading,
    isEFGInternal
  } = useProductEFGInternalStatus();
  const {
    loading: allOptionValuesLoading,
    allOptionsByValuesKey,
    allOptionsValues
  } = useAllOptions();
  let modularCustomerData = useModularPDPCustomerPhotosAndReviews();
  if (!pdpIsModularBYOProduct) {
    modularCustomerData = {};
  }
  const finalReviews = useMemo(() => {
    if (pdpIsModularBYOProduct && !modularCustomerData?.customer_reviews?.reviews || !pdpIsModularBYOProduct && !data?.customerReviews || optionsLoading || EFGLoading || allOptionValuesLoading) {
      return [];
    }
    const productLink = location.pathname.replace('customer-reviews/', '');
    const reviewsList = pdpIsModularBYOProduct ? modularCustomerData.customer_reviews.reviews : data.customerReviews;
    const finalOptions = isEFGInternal ? stock.stockOptions : options;
    // Update material for each review
    return reviewsList.map(review => {
      const finalReview = {
        ...review
      };
      const itemMaterialVal = finalReview?.materials ? JSON.parse(finalReview.materials) : finalReview?.variant ? JSON.parse(finalReview.variant) : null;
      if (!isEmpty(itemMaterialVal)) {
        const optionsList = Object.keys(itemMaterialVal).filter(optKey => ['fabric', 'leather'].includes((optKey || '').trim().toLowerCase()));
        if (optionsList.length) {
          const optionValueName = itemMaterialVal[optionsList[0]];
          let optionValue = getOptionValueByNameAndOptionType({
            optionType: optionsList[0],
            optionValueName,
            options: finalOptions
          });
          finalReview.itemMaterialInactive = false;
          if (!optionValue) {
            const optValueKey = optionValueName.toLowerCase().trim().split(' ').join('-');
            optionValue = allOptionsByValuesKey[optValueKey];
            finalReview.itemMaterialInactive = true;
          }
          finalReview.itemMaterial = optionValue;
          finalReview.materialLink = !finalReview.itemMaterialInactive && optionValue?.value ? `${productLink}?${(optionsList[0] || '').trim().toLowerCase()}=${optionValue.value.toLowerCase().split(' ').join('-')}` : '';
        } else {
          finalReview.itemMaterial = null;
        }
      }
      return finalReview;
    });
  }, [modularCustomerData?.customer_reviews, data?.customerReviews, pdpIsModularBYOProduct, optionsLoading, EFGLoading, stock, allOptionValuesLoading, isEFGInternal, allOptionsByValuesKey, location?.pathname, options]);
  const finalPhotos = useMemo(() => {
    if (pdpIsModularBYOProduct && !modularCustomerData?.customer_photos_min?.photos || !pdpIsModularBYOProduct && !data?.customerPhotos || optionsLoading || EFGLoading || allOptionValuesLoading) {
      return [];
    }
    const productLink_0 = location.pathname.replace('customer-reviews/', '');
    const photosList = pdpIsModularBYOProduct ? modularCustomerData.customer_photos_min.photos : data.customerPhotos;
    return photosList.map(photo => {
      const finalPhoto = {
        ...photo
      };
      if (finalPhoto.upholstery_id && upholsteryOptionId) {
        const itemOptionValue = getSKUForMaterialOrOptionId({
          options: isEFGInternal ? stock.stockOptions : options,
          optionID: parseInt(finalPhoto.upholstery_id, 10),
          optionTypeId: upholsteryOptionId
        });
        const option = getProductOptionById(options, upholsteryOptionId);
        finalPhoto.itemMaterialInactive = false;
        if (itemOptionValue?.upholstery) {
          finalPhoto.itemMaterial = itemOptionValue.upholstery;
          finalPhoto.materialLink = `${productLink_0}?${(option.name || '').trim().toLowerCase()}=${itemOptionValue.upholstery.value.toLowerCase().split(' ').join('-')}`;
        } else {
          finalPhoto.itemMaterial = {
            ...(allOptionsValues[finalPhoto.upholstery_id] || {})
          };
          finalPhoto.itemMaterialInactive = true;
          finalPhoto.materialLink = '';
        }
      }
      return finalPhoto;
    });
  }, [modularCustomerData?.customer_photos_min, data?.customerPhotos, pdpIsModularBYOProduct, optionsLoading, allOptionValuesLoading, options, stock, isEFGInternal, EFGLoading, allOptionsValues, location?.pathname, upholsteryOptionId]);
  return <FullScreenModal isOpen onClose={toggleModal} enableHashHistory={false} mobileNoPadding mobileNoStickyFooterPadding modalPath="reviews" mobileHeaderCenter={productName}>
      <PDPCustomerPhotosReviewsModalUI loading={loading} name={productName} customerReviews={finalReviews || []} customerPhotos={finalPhotos || []} initialImageIndex={imgIndex} initialReviewIndex={reviewIndex} toggleModal={toggleModal} totalReviews={totalReviews} averageRating={averageRating} upholsteryOptionId={upholsteryOptionId} selectedMaterial={selectedMaterial} />
    </FullScreenModal>;
};
PDPCustomerPhotosReviewsModal.propTypes = {
  productId: PropTypes.number,
  productName: PropTypes.string,
  imgIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  reviewIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  toggleModal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalReviews: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  averageRating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedMaterial: PropTypes.object
};
export default withBrowserOnly(PDPCustomerPhotosReviewsModal);